import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, extractError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Send, MessageSquare, Loader2, Users, Star, X } from "lucide-react";

/** Live-group room: Jitsi on the left, side chat + post-session rating on
 * the right. Chat is polled every 3 seconds. */
export default function LiveGroupRoom() {
  const { tid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [chat, setChat] = useState([]);
  const [draft, setDraft] = useState("");
  const [rated, setRated] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);
  const scrollRef = useRef(null);
  const sinceRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.post(`/datp/therapies/${tid}/join`);
        setSession(r.data);
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [tid]);

  // Poll chat every 3s
  useEffect(() => {
    if (!session) return;
    let alive = true;
    const tick = async () => {
      try {
        const params = sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : "";
        const r = await api.get(`/datp/therapies/${tid}/chat${params}`);
        if (!alive) return;
        if (r.data.length) {
          setChat((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const merged = [...prev, ...r.data.filter((m) => !seen.has(m.id))];
            sinceRef.current = merged[merged.length - 1]?.at;
            return merged;
          });
          setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 40);
        }
      } catch {}
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [session, tid]);

  const sendMsg = async () => {
    const t = draft.trim();
    if (!t) return;
    setDraft("");
    try {
      const r = await api.post(`/datp/therapies/${tid}/chat`, { text: t });
      setChat((prev) => [...prev, r.data]);
      sinceRef.current = r.data.at;
      setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 40);
    } catch (e) { toast.error(extractError(e)); }
  };

  const leaveRoom = () => {
    if (!rated) {
      setRatingOpen(true);
    } else {
      nav("/therapies");
    }
  };

  if (error) {
    return (
      <div className="p-8 max-w-md mx-auto text-center">
        <div className="uppercase-label">Cannot join</div>
        <div className="mt-2 text-[color:var(--mano-ink)] font-semibold">{error}</div>
        <button onClick={() => nav("/therapies")} className="mt-6 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] text-white font-semibold">
          Back to therapies
        </button>
      </div>
    );
  }
  if (!session) {
    return (
      <div className="p-8 flex items-center gap-2 text-[color:var(--mano-ink-soft)]">
        <Loader2 size={16} className="animate-spin" /> Joining live group…
      </div>
    );
  }

  const jitsiSrc = `${session.jitsi_url}#config.prejoinPageEnabled=false&userInfo.displayName=${encodeURIComponent(session.display_name)}`;

  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4 h-[calc(100vh-120px)]" data-testid="live-group-room">
      {/* Video pane */}
      <div className="rounded-2xl overflow-hidden bg-black relative">
        <iframe
          title={session.session_title}
          src={jitsiSrc}
          allow="camera; microphone; display-capture; autoplay"
          className="w-full h-full min-h-[420px]"
        />
        <button
          onClick={leaveRoom}
          data-testid="leave-room-btn"
          className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/95 hover:bg-white text-[color:var(--mano-ink)] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
        >
          <ArrowLeft size={12} /> Leave & rate
        </button>
      </div>

      {/* Sidebar: chat + participants */}
      <aside className="rounded-2xl bg-white border border-[color:var(--mano-border)] flex flex-col min-h-0" data-testid="live-chat">
        <header className="p-4 border-b border-[color:var(--mano-border)]">
          <div className="uppercase-label">{session.session_title}</div>
          <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5 inline-flex items-center gap-1">
            <Users size={11} /> {session.participants}/{session.capacity} in room
          </div>
        </header>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[240px]">
          {chat.length === 0 ? (
            <div className="text-center text-xs text-[color:var(--mano-ink-soft)] py-6">
              <MessageSquare size={16} className="mx-auto mb-2 opacity-60" />
              Say hi to the room — the facilitator can see reflections here without you needing to unmute.
            </div>
          ) : (
            chat.map((m) => {
              const mine = m.user_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    mine
                      ? "bg-[color:var(--mano-primary)] text-white rounded-br-md"
                      : "bg-[color:var(--mano-bg)] text-[color:var(--mano-ink)] rounded-bl-md"
                  }`}>
                    {!mine && <div className={`text-[10px] uppercase tracking-wide font-semibold mb-0.5 ${mine ? "text-white/70" : "text-[color:var(--mano-primary)]"}`}>{m.user_name}</div>}
                    <div className="leading-snug break-words">{m.text}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="p-3 border-t border-[color:var(--mano-border)] flex items-center gap-2">
          <input
            data-testid="chat-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMsg()}
            placeholder="Post a reflection…"
            className="flex-1 h-10 px-3 rounded-full border border-[color:var(--mano-border)] bg-white text-sm focus:outline-none focus:border-[color:var(--mano-primary)]"
          />
          <button
            data-testid="chat-send"
            onClick={sendMsg}
            disabled={!draft.trim()}
            className="h-10 w-10 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white flex items-center justify-center disabled:opacity-60"
          >
            <Send size={14} />
          </button>
        </div>
      </aside>

      {ratingOpen && (
        <RatingModal tid={tid} onClose={() => { setRatingOpen(false); nav("/therapies"); }}
                     onDone={() => { setRated(true); setRatingOpen(false); nav("/therapies"); }} />
      )}
    </div>
  );
}

function RatingModal({ tid, onClose, onDone }) {
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (stars < 1) { toast.error("Pick 1–5 stars"); return; }
    setBusy(true);
    try {
      await api.post(`/datp/therapies/${tid}/rate`, { stars, note });
      toast.success("Thanks for the feedback!");
      onDone();
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="rating-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6">
        <div className="flex items-start justify-between">
          <div>
            <span className="uppercase-label">One quick tap</span>
            <h3 className="font-display text-xl mt-1">How was this session?</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="mt-4 flex items-center gap-1 justify-center">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              data-testid={`star-${n}`}
              onClick={() => setStars(n)}
              className="p-1"
            >
              <Star size={30} className={n <= stars ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
            </button>
          ))}
        </div>
        <textarea
          data-testid="rating-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything the facilitator should know? (optional)"
          className="mt-4 w-full h-24 px-3 py-2 rounded-lg border border-[color:var(--mano-border)] bg-white text-sm focus:outline-none focus:border-[color:var(--mano-primary)]"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-[color:var(--mano-border)] text-sm font-semibold">Skip</button>
          <button
            data-testid="rating-submit"
            onClick={submit}
            disabled={busy || !stars}
            className="px-5 py-2 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-sm font-semibold disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
